import { useState, useEffect, useCallback } from "react";
import { fetchUnread } from "../config/api/messengerService";

const POLL_INTERVAL_MS = 45_000;

// Unread status is server-computed (see the messenger backend's read-state model, updated
// whenever someone is a notification recipient) rather than derived from a live feed —
// there's no per-user room index anymore to build a "listen to everything I'm in"
// connection from, so this just polls the cheap /messenger/unread check instead.
export function useUnreadMessages() {
  const [items, setItems] = useState([]);

  const refresh = useCallback(() => {
    fetchUnread()
      .then((data) => setItems(Array.isArray(data?.items) ? data.items : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, POLL_INTERVAL_MS);

    // Two ways to beat the 45s poll instead of leaving the bell stale until it ticks or
    // the page is reloaded: the service worker tells us the moment a push lands (works
    // even with this tab in the background), and refreshing on tab focus catches the case
    // where the user was away when it arrived and just tabbed back.
    const onMessage = (event) => {
      if (event.data?.type === "messenger-push") refresh();
    };
    navigator.serviceWorker?.addEventListener("message", onMessage);

    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      clearInterval(interval);
      navigator.serviceWorker?.removeEventListener("message", onMessage);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [refresh]);

  return { hasUnread: items.length > 0, items, refresh };
}
